<template>
    <div class="ef-login-page">
        <div class="ef-login-page__inner">
            <div class="ef-login-logo">
                <img
                    src="/elite-logo-white.png"
                    alt="ELITE fitness"
                    class="ef-login-logo__img"
                    width="280"
                    height="120"
                    decoding="async"
                />
            </div>

            <p class="ef-login-page__hint">
                {{ token
                    ? 'Scegli una nuova password per il tuo account.'
                    : 'Inserisci la tua email: se è registrata, riceverai il link per reimpostare la password.' }}
            </p>

            <!-- Richiesta del link -->
            <form v-if="!token" class="ef-login-page__form" autocomplete="on" @submit.prevent="resetPassword">
                <div class="ef-login-page__field">
                    <span class="ef-login-page__field-icon" aria-hidden="true">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                        </svg>
                    </span>
                    <label class="visually-hidden" for="reset-email">Email</label>
                    <input
                        id="reset-email"
                        v-model="email"
                        type="email"
                        name="email"
                        autocomplete="email"
                        placeholder="Email"
                        autocapitalize="off"
                        autocorrect="off"
                        spellcheck="false"
                        required
                    />
                </div>

                <p v-if="successMessage" class="ef-login-page__notice">{{ successMessage }}</p>
                <p v-if="errorMessage" class="ef-login-page__error">{{ errorMessage }}</p>

                <button type="submit" class="ef-login-page__submit" :disabled="isLoading">
                    {{ isLoading ? 'Invio…' : 'Invia il link' }}
                </button>
            </form>

            <!-- Nuova password, dal link ricevuto via e-mail -->
            <form v-else class="ef-login-page__form" autocomplete="on" @submit.prevent="updatePassword">
                <div class="ef-login-page__field ef-login-page__field--password">
                    <span class="ef-login-page__field-icon" aria-hidden="true">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                        </svg>
                    </span>
                    <label class="visually-hidden" for="new-password">Nuova password</label>
                    <input
                        id="new-password"
                        v-model="newPassword"
                        :type="showPassword ? 'text' : 'password'"
                        name="new-password"
                        autocomplete="new-password"
                        placeholder="Nuova password"
                        required
                    />
                    <button
                        type="button"
                        class="ef-login-page__toggle"
                        :aria-label="showPassword ? 'Nascondi password' : 'Mostra password'"
                        @click="showPassword = !showPassword"
                    >
                        <svg v-if="showPassword" xmlns="http://www.w3.org/2000/svg" width="22" height="22" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                            <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <svg v-else xmlns="http://www.w3.org/2000/svg" width="22" height="22" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                        </svg>
                    </button>
                </div>

                <div class="ef-login-page__field ef-login-page__field--password">
                    <span class="ef-login-page__field-icon" aria-hidden="true">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                        </svg>
                    </span>
                    <label class="visually-hidden" for="confirm-password">Conferma password</label>
                    <input
                        id="confirm-password"
                        v-model="confirmPassword"
                        :type="showPassword ? 'text' : 'password'"
                        name="confirm-password"
                        autocomplete="new-password"
                        placeholder="Conferma password"
                        required
                    />
                    <button
                        type="button"
                        class="ef-login-page__toggle"
                        :aria-label="showPassword ? 'Nascondi password' : 'Mostra password'"
                        @click="showPassword = !showPassword"
                    >
                        <svg v-if="showPassword" xmlns="http://www.w3.org/2000/svg" width="22" height="22" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                            <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <svg v-else xmlns="http://www.w3.org/2000/svg" width="22" height="22" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                        </svg>
                    </button>
                </div>

                <p v-if="successMessage" class="ef-login-page__notice">{{ successMessage }}</p>
                <p v-if="errorMessage" class="ef-login-page__error">{{ errorMessage }}</p>

                <button v-if="linkExpired" type="button" class="ef-login-page__submit" @click="requestNewLink">
                    Richiedi un nuovo link
                </button>
                <button v-else type="submit" class="ef-login-page__submit" :disabled="isLoading">
                    {{ isLoading ? 'Aggiornamento…' : 'Aggiorna la password' }}
                </button>
            </form>

            <p class="ef-login-page__footer">
                <router-link to="/login" class="ef-login-page__footer-link">Torna all'accesso</router-link>
            </p>
        </div>
    </div>
</template>

<script>
export default {
    data() {
        return {
            email: '',
            newPassword: '',
            confirmPassword: '',
            token: null,
            showPassword: false,
            successMessage: '',
            errorMessage: '',
            isLoading: false,
            linkExpired: false,
        };
    },
    created() {
        this.token = this.$route.query.token || null;
    },
    methods: {
        async resetPassword() {
            this.isLoading = true;
            try {
                const response = await fetch('https://api.elitefitness.it/api/auth/reset-password', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        email: this.email,
                    }),
                });

                if (response.ok) {
                    // Sempre la stessa frase, qualunque cosa sia successo dall'altra parte: il
                    // server risponde allo stesso modo per un indirizzo noto, per uno sconosciuto e
                    // per un guasto della posta. Così questo modulo non può essere usato per
                    // scoprire chi è cliente. «Se l'indirizzo è registrato» e non «ti abbiamo
                    // inviato», perché la seconda sarebbe falsa in due casi su tre.
                    this.successMessage = "Se l'indirizzo è registrato nel sistema, riceverai un'e-mail con il link per reimpostare la password.";
                    this.errorMessage = '';
                } else if (response.status === 429) {
                    this.errorMessage = 'Troppe richieste di reset. Attendi qualche minuto prima di riprovare.';
                    this.successMessage = '';
                } else {
                    this.errorMessage = 'Richiesta non riuscita. Riprova più tardi.';
                    this.successMessage = '';
                }
            } catch (error) {
                console.error('Password reset request failed:', error);
                this.errorMessage = 'Si è verificato un errore durante il reset della password. Riprova più tardi.';
                this.successMessage = '';
            } finally {
                this.isLoading = false;
            }
        },
        async updatePassword() {
            if (this.newPassword !== this.confirmPassword) {
                this.errorMessage = 'Le password non coincidono. Riprova.';
                return;
            }

            this.isLoading = true;
            try {
                const response = await fetch('https://api.elitefitness.it/api/auth/update-password', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        token: this.token,
                        newPassword: this.newPassword,
                    }),
                });

                if (response.ok) {
                    this.successMessage = 'Password aggiornata con successo.';
                    this.errorMessage = '';
                    // Aggiungi un piccolo delay per mostrare il messaggio di successo prima di aprire il deeplink
                    setTimeout(() => {
                        this.openDeepLink();
                    }, 1000);
                } else {
                    // Quasi sempre è il link scaduto (dura un'ora) o già sostituito da una richiesta
                    // successiva: diciamolo chiaro e offriamo subito di richiederne uno nuovo (#119).
                    this.errorMessage = 'Il link per reimpostare la password è scaduto o non è più valido. Richiedine uno nuovo qui sotto.';
                    this.successMessage = '';
                    this.linkExpired = true;
                }
            } catch (error) {
                console.error('Password update failed:', error);
                this.errorMessage = 'Si è verificato un errore durante l\'aggiornamento della password. Riprova più tardi.';
                this.successMessage = '';
            } finally {
                this.isLoading = false;
            }
        },
        requestNewLink() {
            // Torna alla modalità "richiesta link" della stessa pagina (form email), così l'utente
            // può farsene mandare uno nuovo senza cercare un'altra strada. Refs #119
            this.token = null;
            this.linkExpired = false;
            this.errorMessage = '';
            this.successMessage = '';
            this.newPassword = '';
            this.confirmPassword = '';
        },
        openDeepLink() {
            const deeplink = "elitefitness://elitefitness.it";
            const iosStore = "https://apps.apple.com/it/app/id6670614281";
            const androidStore = "https://play.google.com/store/apps/details?id=it.utopialabs.elitefitness";
            const fallbackWeb = "https://elitefitness.it"; // opzionale

            const ua = navigator.userAgent || navigator.vendor || window.opera;
            let storeUrl;

            if (/android/i.test(ua)) {
                storeUrl = androidStore;
            } else if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) {
                storeUrl = iosStore;
            } else {
                // Desktop o altro → vai al sito (puoi cambiarlo in uno store generico)
                window.location = fallbackWeb;
                return;
            }

            const now = Date.now();
            window.location = deeplink;

            setTimeout(() => {
                if (Date.now() - now < 1600) {
                    window.location = storeUrl;
                }
            }, 1500);
        }
    }
};
</script>

<style scoped>
/* Stessa impalcatura della pagina di accesso: stesse classi, stesse misure, stesso gradiente.
   Il reset password era rimasto all'aspetto precedente — riquadro chiaro e stile Bootstrap —
   mentre accesso e area account erano già stati rifatti: chi ci arriva da «Ho dimenticato la
   password» passava da una pagina all'altra e vedeva due prodotti diversi. */

@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700&display=swap');

/* Allineato a elitefitness-react-app/src/pages/LoginPage.css */
.ef-login-page {
    --ef-login-blue: #272d82;
    --ef-login-primary: #272d82;
    --ef-login-input-bg: #ffffff;
    --ef-login-muted: rgba(255, 255, 255, 0.82);
    --ef-safe-top: 0px;
    --ef-safe-left: 0px;
    --ef-safe-right: 0px;
    --ef-safe-bottom: 0px;
    margin-top: calc(-1 * var(--ef-safe-top));
    margin-left: calc(-1 * var(--ef-safe-left));
    margin-right: calc(-1 * var(--ef-safe-right));
    min-height: calc(100dvh + var(--ef-safe-top));
    min-height: calc(100svh + var(--ef-safe-top));
    position: relative;
    overflow: hidden;
    background: linear-gradient(
        125deg,
        #172554 0%,
        #1e3a8a 18%,
        #2563eb 38%,
        #1d4ed8 55%,
        #312e81 72%,
        #1e40af 88%,
        #172554 100%
    );
    background-size: 320% 320%;
    animation: ef-login-page-gradient 12s ease-in-out infinite;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.1);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: calc(32px + var(--ef-safe-top)) calc(24px + var(--ef-safe-right))
        calc(48px + var(--ef-safe-bottom)) calc(24px + var(--ef-safe-left));
    box-sizing: border-box;
    font-family: 'Montserrat', system-ui, sans-serif;
}

@keyframes ef-login-page-gradient {
    0% {
        background-position: 0% 40%;
    }
    50% {
        background-position: 100% 60%;
    }
    100% {
        background-position: 0% 40%;
    }
}

@media (prefers-reduced-motion: reduce) {
    .ef-login-page {
        animation: none;
        background-size: 100% 100%;
        background-position: 50% 50%;
    }
}

.ef-login-page__inner {
    width: 100%;
    max-width: 380px;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 28px;
}

.ef-login-logo {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
}

.ef-login-logo__img {
    display: block;
    width: min(100%, 280px);
    height: auto;
    max-height: min(28dvh, 160px);
    object-fit: contain;
}

.ef-login-page__hint {
    margin: 0;
    text-align: center;
    font-size: 0.9rem;
    color: var(--ef-login-muted);
}

.ef-login-page__form {
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.ef-login-page__field {
    position: relative;
    display: flex;
    align-items: center;
    background: var(--ef-login-input-bg);
    border-radius: 14px;
    border: none;
    min-height: 52px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
}

.ef-login-page__field-icon {
    position: absolute;
    left: 14px;
    display: flex;
    color: #9ca3af;
    pointer-events: none;
}

.ef-login-page__field input {
    width: 100%;
    border: none;
    background: transparent;
    padding: 14px 48px 14px 46px;
    font-size: 1rem;
    font-family: inherit;
    color: #1f2937;
    border-radius: 14px;
    outline: none;
}

.ef-login-page__field input::placeholder {
    color: #9ca3af;
}

.ef-login-page__field--password input {
    padding-right: 48px;
}

.ef-login-page__toggle {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    padding: 0;
    border: none;
    background: transparent;
    color: #9ca3af;
    cursor: pointer;
    border-radius: 10px;
}

.ef-login-page__toggle:hover {
    color: #6b7280;
}

.ef-login-page__toggle:focus-visible {
    outline: 2px solid #fff;
    outline-offset: 2px;
}

.ef-login-page__forgot {
    margin: -4px 0 0;
    text-align: center;
    font-size: 0.88rem;
}

.ef-login-page__forgot :deep(a) {
    padding: 0;
    border: none;
    background: none;
    font: inherit;
    color: rgba(255, 255, 255, 0.95);
    cursor: pointer;
    text-decoration: none;
}

.ef-login-page__forgot :deep(a):hover {
    text-decoration: underline;
}

.ef-login-page__error {
    margin: 0;
    font-size: 0.88rem;
    color: #fecaca;
    text-align: center;
}

.ef-login-page__submit {
    margin-top: 6px;
    width: 100%;
    padding: 15px 20px;
    border: none;
    border-radius: 14px;
    background: #ffffff;
    color: var(--ef-login-primary);
    font-size: 1rem;
    font-weight: 700;
    font-family: inherit;
    cursor: pointer;
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.22);
}

.ef-login-page__submit:disabled {
    opacity: 0.65;
    cursor: not-allowed;
}

.ef-login-page__submit:focus-visible {
    outline: 2px solid #fff;
    outline-offset: 3px;
}

.ef-login-page__footer {
    margin: 0;
    text-align: center;
    font-size: 0.92rem;
    color: rgba(255, 255, 255, 0.92);
}

.ef-login-page__footer :deep(a),
.ef-login-page__footer-link {
    font-weight: 700;
    color: #ffffff;
    text-decoration: none;
    padding: 0;
    border: none;
    background: none;
    font: inherit;
    cursor: pointer;
}

.ef-login-page__footer :deep(a:hover),
.ef-login-page__footer-link:hover {
    text-decoration: underline;
}

/* Area di tocco di «Torna all'accesso» (devops#458): il testo misurava 124x17px. Il padding la porta a 46px di
   altezza e il margine negativo, uguale e contrario, restituisce lo spazio: il link resta dov'era e la riga del
   piè di pagina non cresce. Il selettore ha due classi perché la regola sopra, con `:deep(a)`, ha più peso di
   `.ef-login-page__footer-link` da solo e riporta il padding a 0. */
.ef-login-page__footer .ef-login-page__footer-link {
    display: inline-block;
    padding: 12px 12px;
    margin: -12px -12px;
}


/* L'unica aggiunta rispetto alla pagina di accesso: l'avviso di esito, che lì non serve.
   Verde tenue perché non è un successo da festeggiare — è una conferma di ricezione, e il
   testo dice «se l'indirizzo è registrato», non «fatto». */
.ef-login-page__notice {
    margin: 0;
    font-size: 0.88rem;
    line-height: 1.45;
    color: #d1fae5;
    text-align: center;
}
</style>
